import React, { useState } from "react";
import Box from "@mui/material/Box";
import classes from "./commonModal.module.css";
import { imageBaseURL } from "../../constants/utils";
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import { Dialog, Paper } from "@mui/material";
import DraggablePaper from "./PaperComponent";


const CommonModal = ({
  label,
  open,
  children,
  onClose = () => {},
  sx = {},
  height = "auto",
  width = "none",
  maxWidth = "100%",
  hideBackdrop = false,
  showCrossIcon = true,
  showDownloadIcon = false,
  downloadFilePath,
  downloadFileName,
  isImage = false,
  fullsize = false,
  isDraggable = false
}) => {
  const style = {
    position: isDraggable ? "relative" : "fixed",
    top: isDraggable ? "0" : "50%",
    left: isDraggable ? "36%" : "50%",
    transform: isDraggable ? "none" : "translate(-50%, -50%)",
    width: fullsize ? "100%" : width,
    height: fullsize ? "100%" : height,
    maxWidth: fullsize ? "100%" : maxWidth,
    maxHeight: fullsize ? "100%" : "90vh",
    overflow: "scroll",
    display: "flex",
    flexDirection: "column",
    bgcolor: "background.paper",
    border: "2px solid #fff",
    boxShadow: 24,
    borderRadius: "4px",
    // pt: 2,
    px: 3,
    pb: 2,
  };

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  // Detect file extension
  
  const getExtension = (fileName = "") => {
    return fileName.split(".").pop()?.toLowerCase();
  };
  // Check if file is an image
  const isImageFile = (fileName = "") => {
    const imageExtensions = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "svg"];
    return imageExtensions.includes(getExtension(fileName));
  };

  // Check if file is a pdf
  const isPdfFile = (fileName = "") => {
    const pdfExtensions = ["pdf", "PDF"];
    return pdfExtensions.includes(getExtension(fileName));
  };

  const isPdfFileCheck = isPdfFile(downloadFileName);

  const downloadCsvApi = () => {
    fetch(downloadFilePath, {
      method: "GET",
      headers: {
        "Content-Type": "application/pdf",
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", downloadFileName || "chase_document");

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);
      })
      .catch((err) => {});
  };

  const downloadImage = () => {
    const fileName =
      downloadFileName ||
      downloadFilePath?.split("/")?.pop() ||
      "download_file";

    const imageCheck = isImageFile(fileName);

    fetch(downloadFilePath)
      .then((response) => response.blob())
      .then((blob) => {
        let url;

        // If image: create object URL directly
        if (imageCheck) {
          url = window.URL.createObjectURL(blob);
        } else {
          // If pdf/doc: also works same but this is cleaner for future logic
          url = window.URL.createObjectURL(new Blob([blob]));
        }

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => {});
  };

  const handleDownload = () => {
    const fileName =
      downloadFileName ||
      downloadFilePath?.split("/")?.pop() ||
      "download_file";

    const imageCheck = isImageFile(fileName);

    if (imageCheck) {
      downloadImage();
    } else {
      downloadCsvApi();
    }
  };

  const handleClose = (e) => {
    setZoom(1);
    setRotation(0);
    onClose(e, true);
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableAutoFocus={true}
      disableEnforceFocus={true}
      hideBackdrop={hideBackdrop}
      PaperComponent={isDraggable ? DraggablePaper : Paper}
      maxWidth={false} 
      PaperProps={{
        sx: isDraggable ? {
            maxWidth: 'none', 
            backgroundColor: 'transparent',
            boxShadow: 'none',
        } : {}
      }}
    >
      <Box
        sx={{ ...style, ...sx }}
        className={classes["scroller"]}
        id="draggable-modal-handle"
        style={{ cursor: isDraggable ? 'move' : 'default', paddingBottom: '10px' }}  
      >
        {showCrossIcon ? (
          <>
            <div className={classes["icon"]}>
              <h1
                style={{
                  color: "#505050",
                  fontWeight: "600",
                  fontSize: "1.6rem",
                }}
              >
                {label}
              </h1>
              {isPdfFileCheck || showDownloadIcon ? (
                <img
                  style={{
                    position: "absolute",
                    right: "22px",
                    cursor: "pointer",
                    top: "4px",
                    float: "right",
                  }}
                  src={`${imageBaseURL}/Svg/DownloadIcon.svg`}
                  alt="download icon"
                  loading="lazy"
                  onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                />
              ) : null}
              {isPdfFileCheck ? (
                <div style={{display: "flex", justifyContent: "space-between"}}>
                <img
                  style={{
                    position: "absolute",
                    right: "48px",
                    cursor: "pointer",
                    top: "4px",
                    float: "right",
                    width: "20px",
                  }}
                  src={`${imageBaseURL}/Svg/zoom-plus.svg`}
                  alt="zoomin-icon"
                  loading="lazy"
                  onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(z + 0.2, 3)); }}
                />
                <img
                  style={{
                    position: "absolute",
                    right: "72px",
                    cursor: "pointer",
                    top: "4px",
                    float: "right",
                    width: "20px",
                  }}
                  src={`${imageBaseURL}/Svg/zoom-minus.svg`}
                  alt="zoomout-icon"
                  loading="lazy"
                  onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(z - 0.2, 0.5)); }}
                />
                  <ScreenRotationIcon
                    onClick={(e) => { e.stopPropagation(); setRotation(prev => (prev + 180) % 360); }}
                    sx={{
                      color: "#E52126",
                      position: "absolute",
                      right: "94px",
                      cursor: "pointer",
                      top: "7px",
                      float: "right",
                      width: "30px",
                    }}
                  />
                </div>
              ) : null}              
              <img
                style={{
                  position: "absolute",
                  right: "-5px",
                  cursor: "pointer",
                  top: "6px",
                }}
                src={`${imageBaseURL}/Svg/cross.svg`}
                // width={"100%"}
                // height={"100%"}
                alt="cancel icon"
                loading="lazy"
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
              />
            </div>
          </>
        ) : null}
          <div
            style={{
              transform: `rotate(${rotation}deg) scale(${zoom})`,
              transformOrigin: "center center",
              transition: "transform 0.2s ease",  
            }}
          >
            {children}
          </div>
      </Box>
    </Dialog>
  );
};

export default CommonModal;
